
import React from 'react';
import { BookOpen, Calendar, CheckSquare, PencilLine, Clock, Lightbulb } from 'lucide-react';

const ExamPrep: React.FC = () => {
  return (
    <div className="bg-gray-50 min-h-screen pb-24">
      <section className="bg-blue-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6">Exam Preparation Center</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed opacity-90">
            Strategic guidance to help you dominate your All Board examinations with confidence and efficiency.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: <Calendar />, title: "Study Schedules", desc: "Proven timetables for last-minute prep." },
            { icon: <CheckSquare />, title: "Paper Schemes", desc: "Understand chapter weightage & pairing." },
            { icon: <PencilLine />, title: "Writing Skills", desc: "Master the art of high-scoring board presentation." }
          ].map((item, idx) => (
            <div key={idx} className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100 text-center flex flex-col items-center">
              <div className="bg-blue-50 text-blue-600 p-4 rounded-2xl mb-6">{item.icon}</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 space-y-20">
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Smart Study Strategies</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 mt-1"><Lightbulb className="text-amber-500" /></div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-1">Active Recall Method</h4>
                    <p className="text-slate-600 leading-relaxed">Instead of just reading the notes, test yourself at the end of every chapter. Cover the page and try to recall the key points from our summaries.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="shrink-0 mt-1"><Clock className="text-blue-500" /></div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-1">The 50/10 Rule</h4>
                    <p className="text-slate-600 leading-relaxed">Study for 50 minutes with absolute focus, then take a 10-minute break. This keeps your brain fresh for intensive subjects like Math and Physics.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="shrink-0 mt-1"><CheckSquare className="text-green-500" /></div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg mb-1">Pairing Scheme Awareness</h4>
                    <p className="text-slate-600 leading-relaxed">Always study according to the latest pairing scheme issued by the All Board. Don't spend too much time on chapters that only carry 1-2 marks.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1765020609900-febe9851cb41?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D/800/800" 
                alt="Students Studying Together" 
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
              />
            </div>
          </section>

          <section className="bg-white p-12 rounded-3xl border border-gray-100 shadow-sm">
            <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Mastering Board Paper Presentation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <h4 className="text-xl font-bold text-blue-600 border-b-2 border-blue-100 pb-2">The Golden Rules</h4>
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-center gap-3"><span className="w-2 h-2 bg-blue-600 rounded-full"></span> Use 605 Marker for Main Headings.</li>
                  <li className="flex items-center gap-3"><span className="w-2 h-2 bg-blue-600 rounded-full"></span> Use 604 Marker for Sub-headings.</li>
                  <li className="flex items-center gap-3"><span className="w-2 h-2 bg-blue-600 rounded-full"></span> Leave a line after every short answer.</li>
                  <li className="flex items-center gap-3"><span className="w-2 h-2 bg-blue-600 rounded-full"></span> Draw diagrams with a sharp pencil, label clearly.</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-8 rounded-2xl">
                <h4 className="text-xl font-bold text-slate-900 mb-4">How Notes Help in Exams</h4>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Our EduZone notes are drafted exactly how a board paper should be solved. We use bullet points, proper headings, and highlighted keywords that catch the examiner's eye.
                </p>
                <div className="text-blue-600 font-bold italic">"Presentation is as important as content in All Boards."</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ExamPrep;
